

import { useState } from "react"
import { Link } from "react-router-dom"

const AgreementTable = ({ agreements, onViewUserHistory, onCheckCompliance }) => {
  const [selectedAgreement, setSelectedAgreement] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatCategoryName = (category) => {
    return category.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  }

  const getStatusBadgeClass = (status) => {
    const classes = {
      active: "bg-success",
      superseded: "bg-warning",
      revoked: "bg-danger",
      expired: "bg-secondary",
    }
    return classes[status] || "bg-secondary"
  }

  const getTriggerBadgeClass = (trigger) => {
    const classes = {
      signup: "bg-primary",
      service_creation: "bg-info",
      order_placement: "bg-success",
      order_acceptance: "bg-warning",
      payment_processing: "bg-danger",
      profile_completion: "bg-secondary",
      seller_verification: "bg-dark",
      api_access: "bg-purple",
      manual_acceptance: "bg-orange",
    }
    return classes[trigger] || "bg-secondary"
  }

  const handleViewDetails = (agreement) => {
    setSelectedAgreement(agreement)
    setShowDetailsModal(true)
  }

  const handleViewUserHistory = (userId) => {
    onViewUserHistory(userId)
  }

  const handleCheckCompliance = (userId) => {
    onCheckCompliance(userId)
  }

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover mb-0">
          <thead className="table-light">
            <tr>
              <th>User</th>
              <th>Document</th>
              <th>Category</th>
              <th>Version</th>
              <th>Trigger</th>
              <th>Status</th>
              <th>Accepted At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {agreements.map((agreement) => (
              <tr key={agreement._id}>
                <td>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm me-2">
                      <div className="avatar-title bg-light text-dark rounded-circle">
                        {agreement.userId?.name?.charAt(0) || "U"}
                      </div>
                    </div>
                    <div>
                      <div className="fw-bold">{agreement.userId?.name || "Unknown User"}</div>
                      <small className="text-muted">{agreement.userId?.email}</small>
                      <div>
                        <span
                          className={`badge badge-sm ${agreement.userType === "seller" ? "bg-info" : "bg-primary"}`}
                        >
                          {agreement.userType}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <div>
                    <div className="fw-bold">{agreement.documentId?.title || "Document Deleted"}</div>
                    <small className="text-muted">ID: {agreement.documentId?._id}</small>
                  </div>
                </td>
                <td>
                  <span className="badge bg-secondary">{formatCategoryName(agreement.documentCategory)}</span>
                </td>
                <td>
                  <span className="fw-bold">{agreement.documentVersion}</span>
                </td>
                <td>
                  <span className={`badge ${getTriggerBadgeClass(agreement.trigger)}`}>
                    {agreement.trigger?.replace(/_/g, " ")}
                  </span>
                </td>
                <td>
                  <span className={`badge ${getStatusBadgeClass(agreement.status)}`}>{agreement.status}</span>
                </td>
                <td>
                  <div>
                    <div>{formatDate(agreement.acceptedAt)}</div>
                    <small className="text-muted">via {agreement.acceptanceMethod}</small>
                  </div>
                </td>
                <td>
                  <div className="dropdown">
                    <button
                      className="btn btn-sm btn-outline-secondary dropdown-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                    >
                      Actions
                    </button>
                    <ul className="dropdown-menu">
                      <li>
                        <button className="dropdown-item" onClick={() => handleViewDetails(agreement)}>
                          <i className="fas fa-eye me-2"></i>View Details
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => handleViewUserHistory(agreement.userId?._id)}>
                          <i className="fas fa-history me-2"></i>User History
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => handleCheckCompliance(agreement.userId?._id)}>
                          <i className="fas fa-shield-alt me-2"></i>Check Compliance
                        </button>
                      </li>
                      {agreement.documentId && (
                        <li>
                          <Link
                            className="dropdown-item"
                            to={`/admin/legal-documents/view/${agreement.documentId._id}`}
                          >
                            <i className="fas fa-file-alt me-2"></i>View Document
                          </Link>
                        </li>
                      )}
                    </ul>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Agreement Details Modal */}
      {showDetailsModal && selectedAgreement && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Agreement Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body" style={{ maxHeight: "500px", overflowY: "auto" }}>
                <div className="row">
                  <div className="col-md-6">
                    <h6>User Information</h6>
                    <p>
                      <strong>Name:</strong> {selectedAgreement.userId?.name}
                    </p>
                    <p>
                      <strong>Email:</strong> {selectedAgreement.userId?.email}
                    </p>
                    <p>
                      <strong>User Type:</strong> {selectedAgreement.userType}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <h6>Agreement Information</h6>
                    <p>
                      <strong>Document:</strong> {selectedAgreement.documentId?.title}
                    </p>
                    <p>
                      <strong>Category:</strong> {formatCategoryName(selectedAgreement.documentCategory)}
                    </p>
                    <p>
                      <strong>Version:</strong> {selectedAgreement.documentVersion}
                    </p>
                    <p>
                      <strong>Status:</strong>
                      <span className={`badge ms-2 ${getStatusBadgeClass(selectedAgreement.status)}`}>
                        {selectedAgreement.status}
                      </span>
                    </p>
                  </div>
                </div>

                <hr />

                <div className="row">
                  <div className="col-md-6">
                    <h6>Acceptance Details</h6>
                    <p>
                      <strong>Accepted At:</strong> {formatDate(selectedAgreement.acceptedAt)}
                    </p>
                    <p>
                      <strong>Method:</strong> {selectedAgreement.acceptanceMethod}
                    </p>
                    <p>
                      <strong>Trigger:</strong> {selectedAgreement.trigger}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <h6>Technical Details</h6>
                    <p>
                      <strong>IP Address:</strong> {selectedAgreement.ipAddress}
                    </p>
                    <p>
                      <strong>User Agent:</strong>
                      <small className="text-muted d-block">{selectedAgreement.userAgent}</small>
                    </p>
                  </div>
                </div>

                {selectedAgreement.triggerContext && (
                  <>
                    <hr />
                    <h6>Trigger Context</h6>
                    <pre className="bg-light p-2 rounded">
                      {JSON.stringify(selectedAgreement.triggerContext, null, 2)}
                    </pre>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AgreementTable